import {Trans} from '@lingui/macro';
import {top} from '@popperjs/core';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../../../../libs/shared/ui/src/components/buttons/button';
import {VisualSizesEnum} from '../../../../../../../libs/shared/ui/src/styles/commonStyles';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DraftMessageModel} from '../../../../../../models/api/messageModel';
import {
  ConversationComposerTooltipActionsEnum,
  ConversationComposerTooltipLocationsEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../services/tracking';
import {EducationTooltipCoordinator} from '../../../../../common/tooltips/educationTooltip/educationTooltipCoordinator';

const unknownChannelNamespace = 'unknown';

/*
 * Props.
 */

interface StartPrivateConversationTooltipProps {
  onStartPrivateConversationClick: Handler;
  message: DraftMessageModel;
  // The channelNamespace prop can be undefined if the desired channel is not found in the store.
  // However, we should always attempt to pass it in as a string:
  channelNamespace: string | undefined;
  isPopup: boolean;
  isSharedDraft: boolean;
  iconColor?: string;
}

/*
 * Style.
 */

const StyledDiv = styled.div`
  grid-area: info-tooltip;
  display: flex;
`;

/*
 * Component.
 */

export const PrivateReplyTooltipCoordinator: FC<StartPrivateConversationTooltipProps> = (props) => {
  const theme = useUserTheme();
  const {channelNamespace, isPopup, isSharedDraft, onStartPrivateConversationClick} = props;

  const handleStartPrivateConversationClick = () => {
    trackInterfaceEvent(InterfaceEventsEnum.COMPOSER_TOOLTIP_CLICK, {
      namespace: channelNamespace ?? unknownChannelNamespace,
      action: ConversationComposerTooltipActionsEnum.SEND_PRIVATE_REPLY,
      location: ConversationComposerTooltipLocationsEnum.DRAFT_BANNER,
      is_shared_draft: isSharedDraft,
      is_popup: props.isPopup,
    });

    onStartPrivateConversationClick();
  };

  const handleShow = () => {
    trackInterfaceEvent(InterfaceEventsEnum.COMPOSER_TOOLTIP_VIEW, {
      namespace: channelNamespace ?? unknownChannelNamespace,
      is_shared_draft: isSharedDraft,
      is_popup: isPopup,
    });
  };

  return (
    <StyledDiv>
      <EducationTooltipCoordinator
        interactionId="private-reply-tooltip-info-icon"
        iconColor={props.iconColor || theme.greys.white}
        placement={top}
        titleIcon="lockSmall"
        title={<Trans id="private-reply-tooltip-title">Send a private reply?</Trans>}
        tooltip={
          <Trans id="private-reply-tooltip-body">
            If you want your reply to be private, start a new conversation.
          </Trans>
        }
        buttons={[
          <Button
            size={VisualSizesEnum.SMALL}
            onClick={handleStartPrivateConversationClick}
            interactionId="private-reply-tooltip-button"
          >
            <Trans id="private-reply-tooltip-button">Start new conversation</Trans>
          </Button>,
        ]}
        onShow={handleShow}
      />
    </StyledDiv>
  );
};
